import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Wallet, Plus, Search, Calendar, IndianRupee, TrendingUp,
  Trash2, Edit3, X, Tag, Clock, AlertTriangle, Loader2,
  StickyNote, CalendarDays, BarChart3, ArrowRight
} from 'lucide-react';
import { api } from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { IconInput, IconTextarea } from '../../components/ui/form-inputs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter
} from '../../components/ui/dialog';

interface Expense {
  id: number;
  amount: number;
  label: string;
  expense_date: string;
  notes: string | null;
  created_by: number | null;
  created_at: string;
}

interface ExpenseLabel {
  id: number;
  name: string;
  color: string;
  is_default: number;
}

interface ExpenseSummary {
  today: number;
  yesterday: number;
  thisWeek: number;
  thisMonth: number;
  thisYear: number;
  allTime: number;
  byLabel: { label: string; total: number; count: number }[];
}

type Period = 'today' | 'yesterday' | 'week' | 'month' | 'year' | 'all';

const periodItems: { key: Period; label: string; icon: typeof Clock }[] = [
  { key: 'today', label: 'Today', icon: Clock },
  { key: 'yesterday', label: 'Yesterday', icon: Calendar },
  { key: 'week', label: 'This Week', icon: TrendingUp },
  { key: 'month', label: 'This Month', icon: TrendingUp },
  { key: 'year', label: 'This Year', icon: TrendingUp },
  { key: 'all', label: 'All Time', icon: IndianRupee },
];

function FieldLabel({ label }: { label: string }) {
  return <Label className="text-sm font-medium">{label}</Label>;
}

export default function ExpensesPage() {
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState<Period>('today');
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [deletingExpense, setDeletingExpense] = useState<Expense | null>(null);
  const [showCustomLabelInput, setShowCustomLabelInput] = useState(false);
  const [customLabelName, setCustomLabelName] = useState('');

  const [formAmount, setFormAmount] = useState('');
  const [formLabel, setFormLabel] = useState('');
  const [formDate, setFormDate] = useState(new Date().toISOString().split('T')[0]);
  const [formNotes, setFormNotes] = useState('');

  const { data: summaryData } = useQuery({
    queryKey: ['expense-summary'],
    queryFn: async () => (await api.get<ExpenseSummary>('/api/expenses/summary')).data!,
  });

  const { data: labelsData } = useQuery({
    queryKey: ['expense-labels'],
    queryFn: async () => (await api.get<ExpenseLabel[]>('/api/expenses/labels')).data || [],
  });

  const { data: expensesData, isLoading } = useQuery({
    queryKey: ['expenses', period, selectedLabel],
    queryFn: async () => {
      let url = `/api/expenses?period=${period}&pageSize=200`;
      if (selectedLabel) url += `&label=${encodeURIComponent(selectedLabel)}`;
      return (await api.get<Expense[]>(url)).data || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: { amount: number; label: string; expenseDate: string; notes: string }) => api.post('/api/expenses', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: (q) => {
        const k = q.queryKey[0];
        return typeof k === 'string' && (k === 'expenses' || k === 'expense-summary' || k === 'revenue-page');
      }});
      resetForm();
      setShowAddForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: { id: number; amount: number; label: string; expenseDate: string; notes: string }) => api.patch(`/api/expenses/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: (q) => {
        const k = q.queryKey[0];
        return typeof k === 'string' && (k === 'expenses' || k === 'expense-summary' || k === 'revenue-page');
      }});
      resetForm();
      setEditingExpense(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => api.delete(`/api/expenses/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: (q) => {
        const k = q.queryKey[0];
        return typeof k === 'string' && (k === 'expenses' || k === 'expense-summary' || k === 'revenue-page');
      }});
      setDeletingExpense(null);
    },
  });

  const createLabelMutation = useMutation({
    mutationFn: async (name: string) => api.post('/api/expenses/labels', { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense-labels'] });
      setShowCustomLabelInput(false);
      setCustomLabelName('');
    },
  });

  const labels = labelsData || [];
  const expenses = expensesData || [];
  const summary = summaryData || { today: 0, yesterday: 0, thisWeek: 0, thisMonth: 0, thisYear: 0, allTime: 0, byLabel: [] };

  const labelColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    labels.forEach((l) => { map[l.name] = l.color; });
    return map;
  }, [labels]);

  // Compute label totals from the CURRENT period's expenses (not all-time)
  const labelTotals = useMemo(() => {
    const map: Record<string, number> = {};
    expenses.forEach((e) => {
      map[e.label] = (map[e.label] || 0) + e.amount;
    });
    return map;
  }, [expenses]);

  const filteredExpenses = useMemo(() => {
    if (!searchQuery) return expenses;
    const q = searchQuery.toLowerCase();
    return expenses.filter(
      (e) => e.label.toLowerCase().includes(q) || (e.notes && e.notes.toLowerCase().includes(q))
    );
  }, [expenses, searchQuery]);

  const resetForm = useCallback(() => {
    setFormAmount('');
    setFormLabel('');
    setFormDate(new Date().toISOString().split('T')[0]);
    setFormNotes('');
  }, []);

  const openAdd = useCallback(() => {
    resetForm();
    setShowAddForm(true);
  }, [resetForm]);

  const openEdit = useCallback((expense: Expense) => {
    setFormAmount(String(expense.amount));
    setFormLabel(expense.label);
    setFormDate(expense.expense_date);
    setFormNotes(expense.notes || '');
    setEditingExpense(expense);
  }, []);

  const closeForm = useCallback(() => {
    setShowAddForm(false);
    setEditingExpense(null);
    resetForm();
  }, [resetForm]);

  const handleSubmit = useCallback(() => {
    const amount = parseFloat(formAmount);
    if (!amount || amount <= 0 || !formLabel) return;
    const data = { amount, label: formLabel, expenseDate: formDate, notes: formNotes };
    if (editingExpense) {
      updateMutation.mutate({ id: editingExpense.id, ...data });
    } else {
      createMutation.mutate(data);
    }
  }, [formAmount, formLabel, formDate, formNotes, editingExpense, createMutation, updateMutation]);

  const getColor = (label: string) => labelColorMap[label] || '#6b7280';
  const getColorBg = (label: string) => `${getColor(label)}20`;

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* ── Hero Header ── */}
      <div className="rounded-2xl bg-gradient-to-br from-rose-500 via-rose-600 to-pink-700 p-5 text-white shadow-xl overflow-hidden relative">
        <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-white/15" />
        <div className="relative flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-white/18 flex items-center justify-center backdrop-blur-sm">
              <Wallet className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-white/75">Institute Expenses</p>
              <h1 className="text-2xl font-black mt-1">Daily Expenses</h1>
            </div>
          </div>
          <Button onClick={openAdd} className="h-10 gap-1.5 px-3.5 flex-shrink-0 bg-white/20 hover:bg-white/30 text-white border-white/20">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Expense</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
      </div>

      {/* ── Period Summary Cards ── */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 sm:gap-2">
        {periodItems.map(({ key, label, icon: Icon }) => {
          const isActive = period === key;
          const val = key === 'today' ? summary.today : key === 'yesterday' ? summary.yesterday
            : key === 'week' ? summary.thisWeek : key === 'month' ? summary.thisMonth
            : key === 'year' ? summary.thisYear : summary.allTime;
          return (
            <button
              key={key}
              onClick={() => setPeriod(key)}
              className={`rounded-xl p-2.5 sm:p-3 text-left transition-all border ${
                isActive
                  ? 'bg-rose-50 dark:bg-rose-500/10 border-rose-300 dark:border-rose-500/30 shadow-md ring-1 ring-rose-500/20'
                  : 'bg-light-surface dark:bg-dark-surface border-light-border dark:border-dark-border hover:border-rose-200 dark:hover:border-rose-500/20'
              }`}
            >
              <div className="flex items-center gap-1 mb-1">
                <Icon className={`w-3 h-3 ${isActive ? 'text-rose-500' : 'text-light-text-muted dark:text-dark-text-muted'}`} />
                <p className={`text-[9px] sm:text-[10px] font-medium ${isActive ? 'text-rose-600 dark:text-rose-400' : 'text-light-text-muted'}`}>{label}</p>
              </div>
              <p className={`text-xs sm:text-sm font-bold leading-tight break-all ${isActive ? 'text-rose-700 dark:text-rose-300' : 'text-light-text dark:text-dark-text'}`}>
                {formatCurrency(val)}
              </p>
            </button>
          );
        })}
      </div>

      {/* ── Revenue Button ── */}
      <Link
        to="/revenue"
        className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl text-white shadow-lg active:scale-[0.99] transition-all"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/18 flex items-center justify-center">
            <BarChart3 className="w-5 h-5" />
          </div>
          <div>
            <p className="text-sm font-bold">Revenue Overview</p>
            <p className="text-[10px] text-white/70">Collected, expenses, profit & monthly breakdown</p>
          </div>
        </div>
        <ArrowRight className="w-5 h-5" />
      </Link>

      {/* ── Label Filter ── */}
      <div className="bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border rounded-2xl p-3 sm:p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5">
            <Tag className="w-3.5 h-3.5 text-rose-500" />
            <span className="text-xs font-semibold text-light-text dark:text-dark-text">Categories</span>
            <span className="text-[9px] text-light-text-muted px-1.5 py-0.5 bg-light-elevated dark:bg-dark-elevated rounded-full">
              {period === 'today' ? 'Today' : period === 'yesterday' ? 'Yesterday' : period === 'week' ? 'This Week' : period === 'month' ? 'This Month' : period === 'year' ? 'This Year' : 'All Time'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {selectedLabel && (
              <button onClick={() => setSelectedLabel(null)} className="text-[10px] text-rose-500 hover:text-rose-600 flex items-center gap-0.5">
                <X className="w-3 h-3" /> Clear
              </button>
            )}
            <button
              onClick={() => setShowCustomLabelInput(!showCustomLabelInput)}
              className="text-[10px] font-medium text-rose-500 hover:text-rose-600 flex items-center gap-0.5 transition-colors"
            >
              <Plus className="w-3 h-3" /> Custom
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
          {labels.map((label) => {
            const isActive = selectedLabel === label.name;
            const total = labelTotals[label.name] || 0;
            const color = label.color;
            return (
              <button
                key={label.id}
                onClick={() => setSelectedLabel(isActive ? null : label.name)}
                className={`flex items-center gap-2 p-2.5 rounded-xl text-left transition-all active:scale-95 ${
                  isActive
                    ? 'shadow-md scale-[1.02]'
                    : 'hover:bg-light-elevated dark:hover:bg-dark-elevated'
                }`}
                style={{
                  backgroundColor: isActive ? `${color}20` : undefined,
                  border: `1.5px solid ${isActive ? color : `${color}30`}`,
                }}
              >
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: color }}
                />
                <div className="min-w-0 flex-1">
                  <p className={`text-[10px] font-semibold truncate ${isActive ? '' : 'text-light-text dark:text-dark-text'}`}
                    style={isActive ? { color } : undefined}>
                    {label.name}
                  </p>
                  <p className={`text-[9px] ${total > 0 ? 'text-light-text-muted' : 'text-light-text-muted/50'}`}>
                    {total > 0 ? formatCurrency(total) : '—'}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        <AnimatePresence>
          {showCustomLabelInput && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-light-border dark:border-dark-border">
                <div className="flex-1">
                  <IconInput
                    icon={Tag}
                    value={customLabelName}
                    onChange={(e) => setCustomLabelName(e.target.value)}
                    placeholder="New label name..."
                    onKeyDown={(e) => { if (e.key === 'Enter' && customLabelName.trim()) createLabelMutation.mutate(customLabelName.trim()); }}
                  />
                </div>
                <Button size="sm" className="h-11 px-4 text-xs bg-rose-500 hover:bg-rose-600" onClick={() => customLabelName.trim() && createLabelMutation.mutate(customLabelName.trim())} disabled={!customLabelName.trim() || createLabelMutation.isPending}>
                  {createLabelMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add'}
                </Button>
                <button onClick={() => { setShowCustomLabelInput(false); setCustomLabelName(''); }} className="p-2 hover:bg-light-elevated dark:hover:bg-dark-elevated rounded-lg transition-colors">
                  <X className="w-4 h-4 text-light-text-muted" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Search ── */}
      <IconInput
        icon={Search}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={`Search ${filteredExpenses.length} expenses...`}
      />

      {/* ── Expense List ── */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border rounded-2xl p-4 animate-pulse">
              <div className="flex items-start gap-3">
                <div className="w-1 h-16 bg-light-elevated dark:bg-dark-elevated rounded-full" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-20 bg-light-elevated dark:bg-dark-elevated rounded" />
                  <div className="h-5 w-28 bg-light-elevated dark:bg-dark-elevated rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredExpenses.length === 0 ? (
        <div className="text-center py-16 bg-light-surface dark:bg-dark-surface border border-dashed border-light-border dark:border-dark-border rounded-2xl">
          <div className="w-16 h-16 rounded-2xl bg-rose-50 dark:bg-rose-500/10 mx-auto mb-4 flex items-center justify-center">
            <Wallet className="w-8 h-8 text-rose-400" />
          </div>
          <p className="text-sm font-semibold text-light-text dark:text-dark-text">No expenses yet</p>
          <p className="text-xs text-light-text-muted mt-1 max-w-xs mx-auto">
            {selectedLabel ? 'No expenses match this label. Try another filter.' : 'Tap "Add Expense" to record your first expense.'}
          </p>
          {!selectedLabel && (
            <Button onClick={openAdd} className="mt-4 gap-1.5 bg-rose-500 hover:bg-rose-600">
              <Plus className="w-4 h-4" /> Add Expense
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-1.5">
          {filteredExpenses.map((expense, idx) => {
            const color = getColor(expense.label);
            return (
              <motion.div
                key={expense.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(idx * 0.02, 0.3) }}
                className="bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border rounded-2xl overflow-hidden hover:border-rose-200 dark:hover:border-rose-500/20 transition-colors"
              >
                <div className="flex items-stretch">
                  <div className="w-1 flex-shrink-0" style={{ backgroundColor: color }} />
                  <div className="flex-1 p-3 sm:p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span
                            className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                            style={{ backgroundColor: getColorBg(expense.label), color, border: `1.5px solid ${color}40` }}
                          >
                            {expense.label}
                          </span>
                          <span className="text-[10px] text-light-text-muted dark:text-dark-text-muted flex items-center gap-1">
                            <CalendarDays className="w-3 h-3" />
                            {formatDate(expense.expense_date)}
                          </span>
                        </div>
                        <p className="text-lg sm:text-xl font-black text-light-text dark:text-dark-text leading-tight">
                          {formatCurrency(expense.amount)}
                        </p>
                        {expense.notes && (
                          <p className="text-xs text-light-text-muted dark:text-dark-text-muted mt-1.5 flex items-start gap-1.5">
                            <StickyNote className="w-3 h-3 mt-0.5 flex-shrink-0 text-light-text-muted/50" />
                            <span className="line-clamp-2">{expense.notes}</span>
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-0.5 flex-shrink-0 -mr-1">
                        <button onClick={() => openEdit(expense)} className="p-2 rounded-xl hover:bg-electric-50 dark:hover:bg-electric-500/10 text-light-text-muted hover:text-electric-500 transition-colors" title="Edit">
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeletingExpense(expense)} className="p-2 rounded-xl hover:bg-rose-50 dark:hover:bg-rose-500/10 text-light-text-muted hover:text-rose-500 transition-colors" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ── Add/Edit Modal ── */}
      <Dialog open={showAddForm || !!editingExpense} onOpenChange={(open) => { if (!open) closeForm(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
            <DialogDescription>{editingExpense ? 'Update the expense details.' : 'Record a new daily expense.'}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Amount */}
            <div className="space-y-2">
              <FieldLabel label="Amount (₹)" />
              <IconInput
                icon={IndianRupee}
                type="number"
                value={formAmount}
                onChange={(e) => setFormAmount(e.target.value)}
                placeholder="0"
                className="!text-lg !font-bold"
                min="0"
                step="1"
              />
            </div>

            {/* Label */}
            <div className="space-y-2">
              <FieldLabel label="Category" />
              <div className="flex flex-wrap gap-1.5">
                {labels.map((label) => {
                  const isActive = formLabel === label.name;
                  return (
                    <button
                      key={label.id}
                      type="button"
                      onClick={() => setFormLabel(label.name)}
                      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-semibold transition-all active:scale-95 ${
                        isActive ? 'text-white shadow-md scale-105' : 'hover:scale-[1.02]'
                      }`}
                      style={{
                        backgroundColor: isActive ? label.color : getColorBg(label.name),
                        color: isActive ? 'white' : label.color,
                        border: `1.5px solid ${isActive ? label.color : `${label.color}50`}`,
                      }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: isActive ? 'rgba(255,255,255,0.7)' : label.color }} />
                      {label.name}
                    </button>
                  );
                })}
              </div>
              {!formLabel && <p className="text-[10px] text-rose-500">Select a category</p>}
            </div>

            {/* Date */}
            <div className="space-y-2">
              <FieldLabel label="Date" />
              <input
                type="date"
                value={formDate}
                onChange={(e) => setFormDate(e.target.value)}
                className="w-full h-11 rounded-xl border border-light-border dark:border-dark-border bg-light-elevated dark:bg-dark-elevated px-3 text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-rose-500/50 focus:border-rose-500 mobile-input"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <FieldLabel label="Notes (optional)" />
              <IconTextarea
                icon={StickyNote}
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                placeholder="Add a note..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0 pt-2">
            <Button variant="ghost" onClick={closeForm}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={!formAmount || parseFloat(formAmount) <= 0 || !formLabel || createMutation.isPending || updateMutation.isPending}
              className="bg-rose-500 hover:bg-rose-600 gap-1.5"
            >
              {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {editingExpense ? 'Update' : 'Save Expense'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirmation ── */}
      <Dialog open={!!deletingExpense} onOpenChange={(open) => { if (!open) setDeletingExpense(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-rose-50 dark:bg-rose-500/10 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-rose-500" />
              </div>
              Delete Expense
            </DialogTitle>
            <DialogDescription>This will permanently remove this expense record.</DialogDescription>
          </DialogHeader>

          {deletingExpense && (
            <div className="bg-light-elevated dark:bg-dark-elevated rounded-xl p-3 border border-light-border dark:border-dark-border">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: getColorBg(deletingExpense.label), color: getColor(deletingExpense.label) }}>
                  {deletingExpense.label}
                </span>
                <span className="text-[10px] text-light-text-muted">{formatDate(deletingExpense.expense_date)}</span>
              </div>
              <p className="text-xl font-black text-light-text dark:text-dark-text">{formatCurrency(deletingExpense.amount)}</p>
              {deletingExpense.notes && <p className="text-xs text-light-text-muted mt-1">{deletingExpense.notes}</p>}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setDeletingExpense(null)}>Cancel</Button>
            <Button onClick={() => deletingExpense && deleteMutation.mutate(deletingExpense.id)} disabled={deleteMutation.isPending} className="bg-rose-500 hover:bg-rose-600 gap-1.5">
              {deleteMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
